<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import { useLayoutComponents } from '@/composables/layout.js';
const { TextInput, JsonPreviewModal } = useLayoutComponents();

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: ''
    },
    id: {
        type: String,
        required: true
    }
});

const emit = defineEmits(['update:modelValue']);
const showPreviewModal = ref(false);
const isValidJson = ref(true);
const parsedJson = ref(null);

const togglePreview = () => {
    if (isValidJson.value && parsedJson.value) {
        showPreviewModal.value = true;
    }
};

const validateAndParseJson = (value) => {
    try {
        if (!value) {
            isValidJson.value = true;
            parsedJson.value = null;
            return;
        }
        parsedJson.value = JSON.parse(value);
        isValidJson.value = true;
    } catch (e) {
        isValidJson.value = false;
        parsedJson.value = null;
    }
};

watch(() => props.modelValue, validateAndParseJson, { immediate: true });
</script>

<template>
    <div class="json-input">
        <div class="json-input__field-container">
            <TextInput
                :id="id"
                :model-value="modelValue"
                @update:model-value="emit('update:modelValue', $event)"
                :placeholder="placeholder"
                class="json-input__field"
            />
            <button 
                class="json-input__preview-toggle"
                @click="togglePreview"
                :disabled="!isValidJson || !modelValue"
                :class="{
                    'json-input__preview-toggle--invalid': !isValidJson && modelValue
                }"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                <span class="json-input__preview-label">View JSON</span>
            </button>
        </div>
        <div v-if="!isValidJson && modelValue" class="json-input__error">
            Invalid JSON format
        </div>

        <JsonPreviewModal
            :show="showPreviewModal"
            :json="parsedJson"
            @close="showPreviewModal = false"
        />
    </div>
</template>

<style scoped lang="postcss">
.json-input {
    @apply max-w-7xl;
}

.json-input__field-container {
    @apply flex items-center gap-2 w-full;
}

.json-input__field {
    @apply flex-1;
}



.json-input__preview-toggle {
    @apply inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium self-center
        transition-all duration-200 
        disabled:opacity-50 disabled:cursor-not-allowed
        border border-gray-200 dark:border-gray-700
        text-gray-700 dark:text-gray-300
        hover:border-indigo-300 hover:text-indigo-600 
        dark:hover:border-indigo-500/50 dark:hover:text-indigo-400;
}

.json-input__preview-toggle--invalid {
    @apply border-red-200 text-red-600 hover:border-red-300 hover:text-red-700
        dark:border-red-500/30 dark:text-red-400 dark:hover:border-red-500/50 dark:hover:text-red-400;
}

.json-input__preview {
    @apply p-4 rounded-lg bg-gray-50 dark:bg-gray-900/50 
        border border-gray-200 dark:border-gray-700
        overflow-x-auto;
}

.json-input__preview pre {
    @apply text-sm text-gray-800 dark:text-gray-200;
}

.json-input__error {
    @apply text-sm text-red-600 dark:text-red-400 mt-1;
}
</style> 